import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { 
  ArrowLeft, Edit2, Trash2, MapPin, Home, Maximize, Euro, 
  User, Mail, Phone, Calendar, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProperties, type Property, type PropertyInsert } from "@/hooks/useProperties";
import { PropertyDialog } from "@/components/properties/PropertyDialog";
import { PropertyChecklist } from "@/components/properties/PropertyChecklist";
import { CreatePropertyPortalDialog } from "@/components/properties/CreatePropertyPortalDialog";
import { SEO } from "@/components/seo/SEO";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

const propertyTypeLabels: Record<string, string> = {
  apartment: "Appartement",
  house: "Maison",
  land: "Terrain",
  commercial: "Commerce",
  other: "Autre",
};

const transactionTypeLabels: Record<string, { label: string; color: string }> = {
  sale: { label: "Vente", color: "bg-blue-500/10 text-blue-600" },
  rental: { label: "Location", color: "bg-purple-500/10 text-purple-600" },
};

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  active: { label: "Actif", variant: "default" },
  pending: { label: "En attente", variant: "secondary" },
  sold: { label: "Vendu", variant: "outline" },
  rented: { label: "Loué", variant: "outline" },
  archived: { label: "Archivé", variant: "secondary" },
};

export default function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateProperty, deleteProperty } = useProperties();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: property, isLoading, error } = useQuery({
    queryKey: ["property", id],
    queryFn: async () => {
      if (!id || !user?.id) return null;

      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("id", id)
        .eq("user_id", user.id)
        .single();

      if (error) throw error;
      return data as Property;
    },
    enabled: !!id && !!user?.id,
  });

  const handleUpdate = async (data: PropertyInsert) => {
    if (!id) return;
    await updateProperty.mutateAsync({ id, ...data });
  };

  const handleDelete = async () => {
    if (!id) return;
    await deleteProperty.mutateAsync(id);
    navigate("/immo");
  };

  if (isLoading) {
    return (
      <div className="container max-w-5xl py-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="container max-w-5xl py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-1">Bien non trouvé</h3>
            <p className="text-muted-foreground mb-4">
              Ce bien n'existe pas ou vous n'y avez pas accès.
            </p>
            <Button onClick={() => navigate("/immo")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au portfolio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const transactionConfig = transactionTypeLabels[property.transaction_type] || transactionTypeLabels.sale;
  const statusInfo = statusConfig[property.status] || statusConfig.active;
  const complianceColor = property.compliance_score >= 80 
    ? "text-green-600" 
    : property.compliance_score >= 50 
      ? "text-amber-600" 
      : "text-red-600";

  return (
    <>
      <SEO 
        title={`${property.title} - EasyFinder Immo`}
        description={`Dossier du bien ${property.address}, ${property.city}`}
      />

      <div className="container max-w-5xl py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="space-y-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/immo")}
              className="-ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Portfolio
            </Button>

            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{property.title}</h1>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn("text-xs", transactionConfig.color)}>
                  {transactionConfig.label}
                </Badge>
                <Badge variant={statusInfo.variant} className="text-xs">
                  {statusInfo.label}
                </Badge>
              </div>
            </div>

            <div className="flex items-center gap-1.5 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{property.address}, {property.postal_code} {property.city}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditDialogOpen(true)}>
              <Edit2 className="mr-2 h-4 w-4" />
              Modifier
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer ce bien ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. Tous les documents liés seront détachés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - Checklist */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Dossier documentaire</CardTitle>
              </CardHeader>
              <CardContent>
                <PropertyChecklist 
                  propertyId={property.id}
                  transactionType={property.transaction_type as "sale" | "rental"}
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Compliance score */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className={cn("text-4xl font-bold mb-1", complianceColor)}>
                    {property.compliance_score}%
                  </div>
                  <p className="text-sm text-muted-foreground">Score de conformité</p>
                </div>
                
                <Separator className="my-4" />

                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    <span>{propertyTypeLabels[property.property_type] || property.property_type}</span>
                  </div>
                  {property.surface_m2 && (
                    <div className="flex items-center gap-2">
                      <Maximize className="h-4 w-4 text-muted-foreground" />
                      <span>{property.surface_m2} m²</span>
                      {property.rooms && <span className="text-muted-foreground">• {property.rooms} pièces</span>}
                    </div>
                  )}
                  {property.price && (
                    <div className="flex items-center gap-2">
                      <Euro className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">
                        {new Intl.NumberFormat("fr-FR").format(property.price)} €
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      Créé le {format(new Date(property.created_at), "dd MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client info */}
            {(property.client_name || property.client_email || property.client_phone) && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Client</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {property.client_name && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span>{property.client_name}</span>
                    </div>
                  )}
                  {property.client_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={`mailto:${property.client_email}`}
                        className="text-primary hover:underline"
                      >
                        {property.client_email}
                      </a>
                    </div>
                  )}
                  {property.client_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <a 
                        href={`tel:${property.client_phone}`}
                        className="text-primary hover:underline"
                      >
                        {property.client_phone}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Client portal generation */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Portail client</CardTitle>
              </CardHeader>
              <CardContent>
                <CreatePropertyPortalDialog
                  propertyId={property.id}
                  propertyTitle={property.title}
                  transactionType={property.transaction_type as "sale" | "rental"}
                  clientName={property.client_name}
                  clientEmail={property.client_email}
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit dialog */}
        <PropertyDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          property={property}
          onSubmit={handleUpdate}
        />
      </div>
    </>
  );
}
